import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shuffle, History, Users, User } from 'lucide-react';
import { saveMockSession } from '../mock';

const Home = () => {
  const [lotteryType, setLotteryType] = useState(null); // 'single' or 'paired' or null
  const [setupNumber, setSetupNumber] = useState('');
  const navigate = useNavigate();

  const handleStart = () => {
    const num = parseInt(setupNumber);
    if (num > 0 && num <= 100 && lotteryType) {
      let numberList = [];

      if (lotteryType === 'paired') {
        // Create paired list: [1,1,2,2,3,3,...]
        for (let i = 1; i <= num; i++) {
          numberList.push(i, i);
        }
      } else {
        // Create single list: [1,2,3,4,...]
        for (let i = 1; i <= num; i++) {
          numberList.push(i);
        }
      }

      // Create new session
      const newSession = {
        id: Date.now().toString(),
        type: lotteryType,
        setupNumber: num,
        drawnNumbers: [],
        remainingNumbers: numberList,
        createdAt: new Date().toISOString(),
        completed: false
      };

      saveMockSession(newSession);
      navigate(`/lottery/${newSession.id}`);
    }
  };

  const handleBackToModeSelection = () => {
    setLotteryType(null);
    setSetupNumber('');
  };

  // Show mode selection if no type selected
  if (!lotteryType) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-6xl font-bold mb-4 bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600 bg-clip-text text-transparent">
              Undian Lot | PERLASI NTT
            </h1>
            <p className="text-xl text-gray-600">Pilih jenis undian yang ingin Anda buat</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <Card
              className="border-2 hover:shadow-xl transition-all duration-300 cursor-pointer"
              onClick={() => setLotteryType('single')}
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <User className="w-6 h-6 text-blue-600" />
                  Undian Tunggal
                </CardTitle>
                <CardDescription>Setiap nomor hanya muncul sekali</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg">
                  <p className="text-sm font-medium mb-2">Contoh:</p>
                  <p className="text-xs text-gray-600">Input: 4 → [1, 2, 3, 4]</p>
                  <p className="text-xs text-gray-600">Input: 8 → [1, 2, 3, 4, 5, 6, 7, 8]</p>
                </div>
                <Button
                  className="w-full h-12 text-lg bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                >
                  Pilih Undian Tunggal
                </Button>
              </CardContent>
            </Card>

            <Card
              className="border-2 hover:shadow-xl transition-all duration-300 cursor-pointer"
              onClick={() => setLotteryType('paired')}
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Users className="w-6 h-6 text-orange-600" />
                  Undian Berpasangan
                </CardTitle>
                <CardDescription>Setiap nomor muncul dua kali</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-orange-50 to-pink-50 rounded-lg">
                  <p className="text-sm font-medium mb-2">Contoh:</p>
                  <p className="text-xs text-gray-600">Input: 4 → [1, 1, 2, 2, 3, 3, 4, 4]</p>
                  <p className="text-xs text-gray-600">Input: 5 → [1, 1, 2, 2, 3, 3, 4, 4, 5, 5]</p>
                </div>
                <Button
                  className="w-full h-12 text-lg bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-700 hover:to-pink-700"
                >
                  Pilih Undian Berpasangan
                </Button>
              </CardContent>
            </Card>
          </div>

          <Card className="border-2 hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <History className="w-6 h-6 text-purple-600" />
                Riwayat Sesi
              </CardTitle>
              <CardDescription>Lihat semua sesi undian yang telah dibuat</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => navigate('/sessions')}
                variant="outline"
                className="w-full h-12 text-lg border-2 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-300"
              >
                Lihat Semua Sesi
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show number input form after type selection
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600 bg-clip-text text-transparent">
            Undian Lot | PERLASI NTT
          </h1>
          <p className="text-xl text-gray-600">
            {lotteryType === 'single' ? 'Undian Tunggal' : 'Undian Berpasangan'}
          </p>
        </div>

        <Card className="border-2 hover:shadow-xl transition-all duration-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Shuffle className="w-6 h-6 text-orange-600" />
              Mulai Undian Baru
            </CardTitle>
            <CardDescription>
              Masukkan angka untuk membuat daftar undian {lotteryType === 'single' ? 'tunggal' : 'berpasangan'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Button
              variant="ghost"
              onClick={handleBackToModeSelection}
              className="mb-4"
            >
              ← Kembali ke Pilihan Jenis
            </Button>

            <div className="space-y-2">
              <label className="text-sm font-medium">Masukkan Angka (1-100)</label>
              <Input
                type="number"
                min="1"
                max="100"
                value={setupNumber}
                onChange={(e) => setSetupNumber(e.target.value)}
                placeholder="Contoh: 5"
                className="text-lg h-12"
              />
            </div>

            {setupNumber && parseInt(setupNumber) > 0 && (
              <div className="p-4 bg-gradient-to-r from-orange-100 to-pink-100 rounded-lg animate-in fade-in duration-300">
                <p className="text-sm font-medium mb-2">Daftar yang akan dibuat:</p>
                <p className="text-xs text-gray-600">
                  {lotteryType === 'single'
                    ? `[${Array.from({ length: parseInt(setupNumber) }, (_, i) => i + 1).join(', ')}]`
                    : `[${Array.from({ length: parseInt(setupNumber) }, (_, i) => `${i + 1}, ${i + 1}`).join(', ')}]`
                  }
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Total: {lotteryType === 'single' ? parseInt(setupNumber) : parseInt(setupNumber) * 2} nomor
                </p>
              </div>
            )}

            <Button
              onClick={handleStart}
              disabled={!setupNumber || parseInt(setupNumber) <= 0 || parseInt(setupNumber) > 100}
              className="w-full h-12 text-lg bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-700 hover:to-pink-700 transition-all duration-300"
            >
              Mulai Undian
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Home;